import { useState } from 'react';
import { Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Link } from 'expo-router';
import { colors, control, fontFamily, price, radii, space, spacing, type } from '~/ui/tokens';
import { cityCountryOf } from '../lib/location';
import { formatMoney } from '../lib/money';
import { useCurrency } from '~/stores/useCurrency';
import type { HotelSummary } from '../lib/api/types';

function collectionReviewLabel(score: number) {
  if (score >= 9.5) return 'Exceptional';
  if (score >= 8.5) return 'Excellent';
  if (score >= 8) return 'Very good';
  if (score >= 7) return 'Good';
  return 'Pleasant';
}

export function CollectionHotelCard({
  hotel,
  onPress,
  href,
}: {
  hotel: HotelSummary;
  onPress: () => void;
  href?: string;
}) {
  useCurrency((state) => state.selectedCurrency);
  const [hovered, setHovered] = useState(false);
  const hasReview = typeof hotel.reviewScore === 'number';

  const cardContent = (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`View ${hotel.name}`}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={StyleSheet.flatten([styles.collectionCard, hovered && styles.collectionCardHovered])}
    >
      <View style={styles.collectionImageWrap}>
        {hotel.thumbnail ? (
          <Image source={hotel.thumbnail} style={styles.collectionImage} resizeMode="cover" />
        ) : (
          <View style={[styles.collectionImage, styles.collectionImageEmpty]}>
            <Ionicons name="image-outline" size={26} color={colors.inkFaint} />
          </View>
        )}
        {hasReview ? (
          <View style={styles.collectionReviewBadge} accessibilityLabel={`Guest review score ${hotel.reviewScore}, ${collectionReviewLabel(hotel.reviewScore!)}`}>
            <Text style={styles.collectionReviewScore}>{hotel.reviewScore!.toFixed(1)}</Text>
            <Text style={styles.collectionReviewDescription}>{collectionReviewLabel(hotel.reviewScore!)}</Text>
          </View>
        ) : null}
        <View style={styles.locationPill}>
          <Ionicons name="location" size={12} color={colors.white} />
          <Text style={styles.locationPillText} numberOfLines={1}>{cityCountryOf(hotel.address)}</Text>
        </View>
      </View>

      <View style={styles.collectionCopy}>
        <Text style={styles.collectionName} numberOfLines={2}>{hotel.name}</Text>

        <View style={styles.collectionStars} accessibilityLabel={`${hotel.starRating} star hotel`}>
          {Array.from({ length: Math.min(5, hotel.starRating) }).map((_, index) => (
            <Ionicons key={index} name="star" size={13} color="#F2A900" />
          ))}
        </View>

        <View style={styles.reviewBlock}>
          {hasReview && typeof hotel.reviewCount === 'number' ? (
            <Text style={styles.reviewMetaCount}>{hotel.reviewCount.toLocaleString()} reviews</Text>
          ) : null}
        </View>

        <View style={styles.collectionDivider} />

        <View style={styles.collectionBottom}>
          <View style={styles.collectionPriceCol}>
            <View style={styles.collectionPriceLine}>
              <Text style={styles.collectionPrice}>{formatMoney(hotel.lowestPrice)}</Text>
              <Text style={styles.collectionPerNight}>/night</Text>
            </View>
            <Text style={styles.collectionTaxes}>Online taxes & fees included</Text>
          </View>
          <View style={styles.collectionViewHotel}>
            <Text style={styles.collectionViewHotelText}>View hotel</Text>
          </View>
        </View>
      </View>
    </Pressable>
  );

  return href ? (
    <Link href={href as any} asChild>
      {cardContent}
    </Link>
  ) : (
    cardContent
  );
}

const styles = StyleSheet.create({
  collectionCard: {
    width: 345,
    gap: space.compact,
    transform: [{ translateY: 0 }],
  },
  collectionCardHovered: {
    transform: [{ translateY: -3 }],
  },
  collectionImageWrap: {
    position: 'relative',
    width: '100%',
    aspectRatio: 300 / 205,
  },
  collectionImage: {
    width: '100%',
    height: '100%',
    borderRadius: radii.lg,
    backgroundColor: colors.lineSoft,
  },
  collectionImageEmpty: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  locationPill: {
    position: 'absolute',
    bottom: spacing(1.25),
    left: spacing(1.25),
    maxWidth: '86%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.micro,
    borderRadius: radii.full,
    backgroundColor: 'rgba(15, 23, 42, 0.74)',
    paddingVertical: spacing(0.75),
    paddingHorizontal: spacing(1.25),
  },
  locationPillText: {
    flexShrink: 1,
    fontFamily: fontFamily.medium,
    fontSize: control.badge.fontSize,
    color: colors.white,
  },
  collectionReviewBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: colors.brand,
  },
  collectionReviewScore: {
    fontFamily: fontFamily.semibold,
    fontSize: 14,
    color: colors.white,
    fontVariant: ['tabular-nums'],
  },
  collectionReviewDescription: {
    fontFamily: fontFamily.semibold,
    fontSize: 12,
    color: colors.white,
  },
  collectionCopy: {
    gap: space.related,
  },
  collectionName: {
    height: type.cardTitle.lineHeight * 2,
    flexShrink: 0,
    fontFamily: fontFamily.bold,
    ...type.cardTitle,
    color: colors.ink,
  },
  collectionStars: {
    flexDirection: 'row',
    gap: 2,
  },
  reviewBlock: {
    height: 36,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.related,
  },
  reviewMetaCount: {
    fontFamily: fontFamily.regular,
    fontSize: type.metadata.fontSize,
    color: colors.inkFaint,
  },
  collectionDivider: {
    height: 1,
    backgroundColor: colors.lineSoft,
  },
  collectionBottom: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(1),
  },
  collectionPriceCol: {
    minWidth: 0,
  },
  collectionPriceLine: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: space.micro,
  },
  collectionPrice: {
    fontFamily: fontFamily.bold,
    ...price.compact,
    color: colors.ink,
  },
  collectionPerNight: {
    fontFamily: fontFamily.regular,
    fontSize: type.metadata.fontSize,
    color: colors.inkFaint,
  },
  collectionTaxes: {
    marginTop: space.micro,
    fontFamily: fontFamily.regular,
    fontSize: type.metadata.fontSize,
    color: colors.inkFaint,
  },
  collectionViewHotel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.micro,
  },
  collectionViewHotelText: {
    fontFamily: fontFamily.semibold,
    fontSize: control.cardCta.fontSize,
    color: colors.brand,
  },
});
